import { useState } from 'react';
import type { ItemWrites } from '../item/item-writes';
import type { DataIndexItemDetail } from '../item/hooks';
import { SENSITIVE_PRIVACY_LEVEL } from '../utils/types';
import type { ItemDetailsDraft } from './edit-details';

/**
 * The drawer's two modes.
 *
 * A draft is what "editing" means here: it opens on what the row shows, it
 * is the only thing the form writes to, and it survives nothing — cancelling
 * throws it away, and a failed save keeps it so the same values can be sent
 * again.
 */

export type DrawerEditing = {
  /** Null while the drawer is only being read. */
  draft: ItemDetailsDraft | null;
  isSaving: boolean;
  /**
   * A tag is being created. Saving mid-name would write the set without the
   * new tag and end the edit, leaving the tag created and attached to
   * nothing — the same reason the list's bulk bar waits.
   */
  isNaming: boolean;
  setIsNaming: (isNaming: boolean) => void;
  start: () => void;
  change: (next: ItemDetailsDraft | ((current: ItemDetailsDraft) => ItemDetailsDraft)) => void;
  cancel: () => void;
  save: () => Promise<void>;
};

type DrawerEditingInput = {
  item: DataIndexItemDetail;
  /**
   * The description the drawer shows. The draft opens on it and `saveDetails`
   * compares against it, so the two are the same string by construction: a
   * row described only by generated copy would otherwise open blank and save
   * nothing.
   */
  description: string;
  writes: ItemWrites;
};

export function useDrawerEditing({ item, description, writes }: DrawerEditingInput): DrawerEditing {
  const [draft, setDraft] = useState<ItemDetailsDraft | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isNaming, setIsNaming] = useState(false);

  const save = async () => {
    if (draft == null || isNaming) {
      return;
    }
    setIsSaving(true);
    const saved = await writes.saveDetails(draft);
    setIsSaving(false);

    if (saved) {
      setDraft(null);
    }
  };

  return {
    draft,
    isSaving,
    isNaming,
    setIsNaming,
    start: () => {
      // Whatever the last edit left behind, including a tag half-named when it
      // was abandoned: this edit starts from the row.
      setIsNaming(false);
      setDraft({
        description,
        tagIds: [...item.tagIds],
        isSensitive: item.privacyLevel === SENSITIVE_PRIVACY_LEVEL,
      });
    },
    // Takes an updater as well as a value: a tag being created lands after an
    // await, by which time a chip removed in the meantime would be back if the
    // update were applied to the draft captured when the picker rendered.
    change: (next) =>
      setDraft((current) => (current == null ? current : typeof next === 'function' ? next(current) : next)),
    cancel: () => {
      setIsNaming(false);
      setDraft(null);
    },
    save,
  };
}
